import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Plus, Users, Calendar, Briefcase, Download, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading-states';
import Lottie from 'lottie-react';
import loadingAnimation from '@/assets/animations/loading-blue.json';
import { useDepartmentsForSelect } from '@/hooks/useDepartments';
import { useOrganizationsForSelect } from '@/hooks/organizations/useOrganizations';
import { useDepartmentMonthSchedule } from '@/hooks/schedules/useDepartmentSchedule';
import { ScheduleGrid } from './ScheduleGrid';
import { TemplatesList } from '@/components/schedules/templates/TemplatesList';
import { ScheduleCalendar } from '@/components/schedules/calendar/ScheduleCalendar';
import { AssignEmployeesDialog } from '@/components/schedules/templates/AssignEmployeesDialog';
import { useScheduleTemplate } from '@/hooks/schedules/useScheduleTemplates';
import type { ScheduleDay, EmployeeSchedule, ScheduleTemplate } from '@/types/schedule.types';
import { exportScheduleToExcel } from '@/utils/exportScheduleToExcel';

const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

// Data passed to CreateExceptionDialog for replacement filtering
export interface CreateExceptionData {
  employeeId: string;
  date: string;
  employeeName: string;
  // All employees with their schedules for the selected date
  employeesWithSchedule: Array<{
    id: string;
    name: string;
    position?: string | null;
    isWorkingDay: boolean;
    shiftStart?: string | null;
    shiftEnd?: string | null;
  }>;
}

interface DepartmentScheduleViewProps {
  onCreateException?: (data: CreateExceptionData) => void;
  onViewException?: (exceptionId: string) => void;
}

export const DepartmentScheduleView: React.FC<DepartmentScheduleViewProps> = ({
  onCreateException,
  onViewException,
}) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<ScheduleTemplate | null>(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [templateToAssign, setTemplateToAssign] = useState<ScheduleTemplate | null>(null);
  const templatesPanelRef = useRef<HTMLDivElement>(null);

  // Fetch organizations for filter
  const { data: organizations, isLoading: isLoadingOrganizations } = useOrganizationsForSelect();

  // Fetch departments filtered by organization
  const { data: departments, isLoading: isLoadingDepartments } = useDepartmentsForSelect(
    selectedOrganizationId
  );

  // Hide empty departments, but always keep the currently selected one visible
  const visibleDepartments = useMemo(() => {
    if (!departments) return [];
    return departments.filter(
      (d) => (d.employee_count ?? 0) > 0 || d.id === selectedDepartmentId,
    );
  }, [departments, selectedDepartmentId]);

  // Reset department and position when organization changes
  const handleOrganizationChange = (value: string) => {
    const newOrgId = value === 'all' ? null : value;
    setSelectedOrganizationId(newOrgId);
    setSelectedDepartmentId(null); // Сброс выбранного отдела
    setSelectedPositionId(null); // Сброс выбранной должности
  };

  // Reset position when department changes
  const handleDepartmentChange = (value: string) => {
    setSelectedDepartmentId(value || null);
    setSelectedPositionId(null); // Сброс выбранной должности
  };

  // Fetch department schedule
  const {
    data: scheduleData,
    isLoading: isLoadingSchedule,
    isFetching: isFetchingSchedule,
  } = useDepartmentMonthSchedule(selectedDepartmentId, year, month);

  // Background refetch (e.g. after creating an exception) — data is already on
  // screen (isLoading=false) but stale while the grid re-resolves. Show a
  // non-blocking overlay so the user knows the change is being applied.
  const isRefreshingSchedule = isFetchingSchedule && !isLoadingSchedule;

  // Extract unique positions from loaded employees
  const positions = useMemo(() => {
    if (!scheduleData?.employees) return [];

    const posMap = new Map<string, string>();
    for (const emp of scheduleData.employees) {
      if (emp.position?.id && emp.position?.name) {
        posMap.set(emp.position.id, emp.position.name);
      }
    }

    return Array.from(posMap, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  }, [scheduleData]);

  // Filter employees by selected position
  const filteredEmployees = useMemo(() => {
    if (!scheduleData?.employees) return [];
    if (!selectedPositionId) return scheduleData.employees;

    return scheduleData.employees.filter(
      (emp) => emp.position?.id === selectedPositionId
    );
  }, [scheduleData, selectedPositionId]);

  // Reset position filter if selected position doesn't exist in new data
  useEffect(() => {
    if (selectedPositionId && positions.length > 0) {
      const positionExists = positions.some((p) => p.id === selectedPositionId);
      if (!positionExists) {
        setSelectedPositionId(null);
      }
    }
  }, [positions, selectedPositionId]);

  // Fetch the selected employee's full template
  const selectedEmployeeTemplateId = useMemo(() => {
    if (!selectedEmployeeId || !scheduleData?.employees) return null;
    const emp = scheduleData.employees.find(e => e.employee_id === selectedEmployeeId);
    return emp?.schedule_template?.id || null;
  }, [selectedEmployeeId, scheduleData]);

  const { data: employeeTemplateData } = useScheduleTemplate(selectedEmployeeTemplateId);

  // Auto-select template when employee's template is loaded
  useEffect(() => {
    if (employeeTemplateData) {
      setSelectedTemplate(employeeTemplateData);
    }
  }, [employeeTemplateData]);

  // Handle employee click — select their template in the panel
  const handleEmployeeClick = (employee: EmployeeSchedule) => {
    setSelectedEmployeeId(employee.employee_id);
    if (employee.schedule_template) {
      // Immediately set a partial template so the calendar starts loading
      setSelectedTemplate((prev) => {
        if (prev?.id === employee.schedule_template!.id) return prev;
        return {
          id: employee.schedule_template!.id,
          name: employee.schedule_template!.name,
        } as ScheduleTemplate;
      });
    } else {
      setSelectedTemplate(null);
    }
    // Scroll templates panel into view
    templatesPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // Generate dates for the month
  const dates = useMemo(() => {
    const result: Date[] = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      result.push(new Date(d));
    }

    return result;
  }, [year, month]);

  // Navigate months
  const goToPrevMonth = () => {
    if (month === 0) {
      setYear(year - 1);
      setMonth(11);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 11) {
      setYear(year + 1);
      setMonth(0);
    } else {
      setMonth(month + 1);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  };

  const handleExportExcel = async () => {
    if (!scheduleData) return;
    await exportScheduleToExcel({
      employees: filteredEmployees,
      dates,
      departmentName: scheduleData.department_name,
      year,
      month,
    });
  };

  const handleAssignTemplate = (template: ScheduleTemplate) => {
    setTemplateToAssign(template);
    setIsAssignDialogOpen(true);
  };

  // Handle cell click
  const handleCellClick = (employee: EmployeeSchedule, day: ScheduleDay) => {
    if (day.exception) {
      // View existing exception
      onViewException?.(day.exception.id);
    } else {
      // Create new exception - pass all employees with their schedules for this date
      const employeesWithSchedule = scheduleData?.employees.map((emp) => {
        const empDaySchedule = emp.schedule.find((s) => s.date === day.date);
        return {
          id: emp.employee_id,
          name: `${emp.last_name} ${emp.first_name}`,
          position: emp.position?.name ?? null,
          isWorkingDay: empDaySchedule?.is_working_day ?? false,
          shiftStart: empDaySchedule?.shift_start,
          shiftEnd: empDaySchedule?.shift_end,
        };
      }) ?? [];

      onCreateException?.({
        employeeId: employee.employee_id,
        date: day.date,
        employeeName: `${employee.last_name} ${employee.first_name}`,
        employeesWithSchedule,
      });
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (!scheduleData) return { totalEmployees: 0, workingToday: 0, exceptionsToday: 0 };

    const today = new Date().toISOString().split('T')[0];
    let workingToday = 0;
    let exceptionsToday = 0;

    for (const emp of scheduleData.employees) {
      const todaySchedule = emp.schedule.find((s) => s.date === today);
      if (todaySchedule?.is_working_day) workingToday++;
      if (todaySchedule?.source === 'schedule_exception') exceptionsToday++;
    }

    return {
      totalEmployees: scheduleData.total_employees,
      workingToday,
      exceptionsToday,
    };
  }, [scheduleData]);

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      {/* Header with organization/department selectors and navigation */}
      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-4">
          {/* Organization selector */}
          <Select
            value={selectedOrganizationId || 'all'}
            onValueChange={handleOrganizationChange}
          >
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Все организации" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все организации</SelectItem>
              {isLoadingOrganizations ? (
                <div className="p-2 flex justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                organizations?.map((org: { id: string; name: string }) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>

          {/* Department selector */}
          <Select
            value={selectedDepartmentId || ''}
            onValueChange={handleDepartmentChange}
          >
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder="Выберите отдел" />
            </SelectTrigger>
            <SelectContent>
              {isLoadingDepartments ? (
                <div className="p-2 flex justify-center">
                  <LoadingSpinner size="sm" />
                </div>
              ) : visibleDepartments.length > 0 ? (
                visibleDepartments.map(
                  (dept: { id: string; name: string; employee_count?: number }) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      <span className="inline-flex items-center gap-2">
                        <span>{dept.name}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {dept.employee_count ?? 0}
                        </span>
                      </span>
                    </SelectItem>
                  ),
                )
              ) : (
                <div className="p-2 text-sm text-muted-foreground text-center">
                  {departments && departments.length > 0
                    ? 'Нет отделов с сотрудниками'
                    : 'Нет отделов'}
                </div>
              )}
            </SelectContent>
          </Select>

          {/* Position filter */}
          <Select
            value={selectedPositionId || 'all'}
            onValueChange={(value) => setSelectedPositionId(value === 'all' ? null : value)}
            disabled={!scheduleData}
          >
            <SelectTrigger className="w-[200px]">
              <div className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-muted-foreground" />
                <SelectValue placeholder="Все должности" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все должности</SelectItem>
              {positions.map((pos) => (
                <SelectItem key={pos.id} value={pos.id}>
                  {pos.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Month navigation */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 min-w-[140px] justify-center">
            <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="font-medium whitespace-nowrap">
              {MONTH_NAMES[month]} {year}
            </span>
          </div>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {scheduleData && filteredEmployees.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <Download className="h-4 w-4 mr-1" />
              Excel
            </Button>
          )}
        </div>
      </div>

      {/* Statistics cards */}
      {selectedDepartmentId && scheduleData && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Сотрудников</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalEmployees}</div>
              <p className="text-xs text-muted-foreground">
                в отделе {scheduleData.department_name}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Работают сегодня</CardTitle>
              <Calendar className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.workingToday}</div>
              <p className="text-xs text-muted-foreground">
                {stats.totalEmployees - stats.workingToday} выходных
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Исключений сегодня</CardTitle>
              <Plus className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">{stats.exceptionsToday}</div>
              <p className="text-xs text-muted-foreground">изменения в графике</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Schedule grid */}
      {!selectedDepartmentId ? (
        <Card className="flex-1 min-h-0">
          <CardContent className="flex items-center justify-center h-full">
            <div className="text-center text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Выберите отдел для просмотра графика</p>
            </div>
          </CardContent>
        </Card>
      ) : isLoadingSchedule ? (
        <Card className="flex-1 min-h-0">
          <CardContent className="flex items-center justify-center h-full">
            <Lottie animationData={loadingAnimation} loop className="w-48 h-48" />
          </CardContent>
        </Card>
      ) : scheduleData ? (
        <div className="flex flex-col gap-4">
          <div className="relative h-[55vh] min-h-[300px] flex flex-col">
            <div
              className={cn(
                'flex-1 min-h-0 flex flex-col transition-opacity',
                isRefreshingSchedule && 'opacity-50 pointer-events-none',
              )}
            >
              <ScheduleGrid
                employees={filteredEmployees}
                dates={dates}
                onCellClick={handleCellClick}
                onEmployeeClick={handleEmployeeClick}
                selectedEmployeeId={selectedEmployeeId}
              />
            </div>
            {isRefreshingSchedule && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 rounded-full bg-background/95 border shadow-sm px-3 py-1.5 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Обновление графика…
              </div>
            )}
          </div>
          {/* Templates panel — same layout as Templates tab */}
          <div ref={templatesPanelRef} className="h-[600px] shrink-0 border rounded-lg overflow-hidden flex">
            <div className="w-80 flex-shrink-0 h-full overflow-hidden">
              <TemplatesList
                selectedTemplateId={selectedTemplate?.id || null}
                onSelectTemplate={setSelectedTemplate}
                onAssignTemplate={handleAssignTemplate}
              />
            </div>
            <div className="flex-1 overflow-hidden">
              <ScheduleCalendar
                selectedTemplate={selectedTemplate}
              />
            </div>
          </div>
        </div>
      ) : null}

      <AssignEmployeesDialog
        open={isAssignDialogOpen}
        onOpenChange={setIsAssignDialogOpen}
        template={templateToAssign}
      />
    </div>
  );
};

export default DepartmentScheduleView;
